import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Center,
  ScrollView,
  useToast,
  Spinner,
  Pressable,
  Icon,
  Input,
  Image,
  Modal,
} from "native-base";
import { GlobalStyles } from "../../constants/styles";
import { useSelector } from "react-redux";
import axios from "axios";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import Toast from "../../components/Toast";
import { GlobalConstants } from "../../constants/constants";


const API_BASE_URL = `${GlobalConstants.ReservationBaseUrl}`.replace(/\/$/, "");

const GRAD_START = "#000561";
const GRAD_END   = "#0042C7";
const MUSTARD    = "#FFC107";
const BLUE_DARK  = "#030057";


const GradientActionButton = ({ label, onPress, isDisabled = false }) => (
  <Pressable onPress={onPress} disabled={isDisabled}>
    <LinearGradient
      colors={isDisabled ? ["#CCCCCC", "#999999"] : [GRAD_START, GRAD_END]}
      start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
      style={{
        borderRadius: 10, width: 190, height: 40,
        justifyContent: "center", alignItems: "center",
      }}
    >
      <Text fontWeight="bold" fontSize="sm" color={MUSTARD} fontFamily={GlobalStyles.text.fontFamily}>
        {label}
      </Text>
    </LinearGradient>
  </Pressable>
);

const SmallGradientButton = ({ label, onPress, isLoading = false, disabled = false }) => (
  <Pressable onPress={onPress} disabled={disabled || isLoading}>
    <Box borderRadius={4} overflow="hidden" opacity={disabled || isLoading ? 0.55 : 1}>
      <LinearGradient
        colors={[GRAD_START, GRAD_END]}
        start={{ x: 0, y: 0 }} end={{ x: 0, y: 1 }}
        style={{ paddingVertical: 8, paddingHorizontal: 20, alignItems: "center", minWidth: 64 }}
      >
        {isLoading
          ? <Spinner color={MUSTARD} size="sm" />
          : <Text fontSize="13px" fontWeight="500" color={MUSTARD} fontFamily={GlobalStyles.text.fontFamily}>{label}</Text>
        }
      </LinearGradient>
    </Box>
  </Pressable>
);

const OutlineButton = ({ label, onPress, disabled = false }) => (
  <Pressable onPress={onPress} disabled={disabled}>
    <Box borderRadius={4} borderWidth={1} borderColor={BLUE_DARK} px={4} py={1.5} bg="white" opacity={disabled ? 0.5 : 1}>
      <Text fontSize="13px" fontWeight="500" color={BLUE_DARK} fontFamily={GlobalStyles.text.fontFamily}>{label}</Text>
    </Box>
  </Pressable>
);

const HomeButton = ({ onPress }) => (
  <Pressable onPress={onPress}>
    <HStack alignItems="center" space={1} bg="rgba(3,0,87,0.08)" px={3} py={1.5} borderRadius={8}>
      <Icon as={Ionicons} name="home" size="sm" color={BLUE_DARK} />
      <Text fontSize="12px" fontWeight="500" color={BLUE_DARK} fontFamily={GlobalStyles.text.fontFamily}>Home</Text>
    </HStack>
  </Pressable>
);

const TopRow = ({ onHome }) => (
  <HStack w="100%" justifyContent="flex-end" alignItems="center" mb={2}>
    <HomeButton onPress={onHome} />
  </HStack>
);

const BottomRow = ({ onBack, right }) => (
  <HStack w="100%" justifyContent="space-between" alignItems="center" mt={4}>
    {onBack ? <OutlineButton label="Back" onPress={onBack} /> : <Box />}
    {right  ? right : <Box />}
  </HStack>
);

const SearchBar = ({ value, onChange, onClear, onSubmit }) => (
  <HStack
    flex={1} borderRadius={8} borderWidth={1} borderColor="#DDD"
    px={2} py={1.5} alignItems="center" space={1} bg="white"
  >
    <Icon as={Ionicons} name="search" color="#999" size="sm" />
    <Input
      flex={1} variant="unstyled"
      value={value} onChangeText={onChange}
      placeholder="Search by name or ID"
      placeholderTextColor="#AAA"
      fontSize="12px"
      onSubmitEditing={onSubmit}
      returnKeyType="search"
      fontFamily={GlobalStyles.text.fontFamily}
    />
    {value?.length > 0 && (
      <Pressable onPress={onClear}>
        <Icon as={Ionicons} name="close-circle" color="#AAA" size="sm" />
      </Pressable>
    )}
  </HStack>
);

const DetailRow = ({ label, value, valueColor }) => (
  <HStack justifyContent="space-between" py={0.5}>
    <Text fontSize="12px" color="#666" minW="100px" fontFamily={GlobalStyles.text.fontFamily}>{label}</Text>
    <Text fontSize="12px" fontWeight="500" color={valueColor || "#000"} flex={1} textAlign="right" ml={2} fontFamily={GlobalStyles.text.fontFamily}>{value}</Text>
  </HStack>
);

const HomeScreen = ({ onNavigate }) => (
  <VStack alignItems="center" space={3} mt={4} w="100%">
    <GradientActionButton label="Make Reservation"   onPress={() => onNavigate("selectDate")} />
    <GradientActionButton label="View Reservation"   onPress={() => onNavigate("viewReservation")} />
    <GradientActionButton label="Cancel Reservation" onPress={() => onNavigate("cancelReservation")} />
  </VStack>
);


const SelectDateScreen = ({ onNavigate, onDateSelect }) => {
  const [currentDate,  setCurrentDate]  = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const daysOfWeek = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const today      = new Date(); today.setHours(0,0,0,0);

  const getDaysInMonth = (date) => {
    const year  = date.getFullYear();
    const month = date.getMonth();
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    const start = first.getDay() === 0 ? 6 : first.getDay() - 1;
    const days  = [];
    for (let i = 0; i < start; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(d);
    return days;
  };

  const isPast = (day) => {
    if (!day) return false;
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    d.setHours(0,0,0,0);
    return d < today;
  };

  const isSelected = (day) => {
    if (!selectedDate || !day) return false;
    return selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentDate.getMonth() &&
      selectedDate.getFullYear() === currentDate.getFullYear();
  };

  const handleDayPress = (day) => {
    if (!day || isPast(day)) return;
    setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
  };

  const handleNext = () => {
    if (!selectedDate) return;
    const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const d = String(selectedDate.getDate()).padStart(2, "0");
    onDateSelect(`${selectedDate.getFullYear()}-${m}-${d}`);
    onNavigate("selectTime");
  };

  const days = getDaysInMonth(currentDate);

  return (
    <VStack w="100%" alignItems="center" space={3}>
      <TopRow onHome={() => onNavigate("home")} />
      <VStack w="100%" maxW="360px" alignSelf="center" space={3}>
        <Text fontSize="15px" fontWeight="600" color="#000" textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>
          Select Date
        </Text>

        <Box w="100%" overflow="hidden" borderRadius={8}>
          <LinearGradient colors={["#000561", "rgba(0,122,255,0.58)"]} start={{ x:0, y:0 }} end={{ x:1, y:0 }}>
            <HStack justifyContent="space-between" alignItems="center" px={3} py={2.5}>
              <Pressable onPress={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))} hitSlop={8}>
                <Icon as={Ionicons} name="chevron-back" size="md" color="white" />
              </Pressable>
              <Text fontSize="13px" fontWeight="500" color="white" fontFamily={GlobalStyles.text.fontFamily}>
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </Text>
              <Pressable onPress={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))} hitSlop={8}>
                <Icon as={Ionicons} name="chevron-forward" size="md" color="white" />
              </Pressable>
            </HStack>
          </LinearGradient>
        </Box>

        <HStack justifyContent="space-around" w="100%">
          {daysOfWeek.map(d => (
            <Box key={d} flex={1} alignItems="center">
              <Text fontSize="11px" fontWeight="500" color="#555">{d}</Text>
            </Box>
          ))}
        </HStack>

        <VStack space={0.5} w="100%">
          {Array.from({ length: Math.ceil(days.length / 7) }).map((_, wk) => (
            <HStack key={wk} justifyContent="space-around" w="100%">
              {days.slice(wk * 7, wk * 7 + 7).map((day, di) => {
                const sel  = isSelected(day);
                const past = isPast(day);
                return (
                  <Pressable key={di} onPress={() => handleDayPress(day)} flex={1} disabled={!day || past}>
                    <Box h="36px" justifyContent="center" alignItems="center" borderRadius="full" overflow="hidden">
                      {sel ? (
                        <LinearGradient
                          colors={["#2196F3","#1976D2"]}
                          start={{ x:0,y:0 }} end={{ x:1,y:1 }}
                          style={{ width:34, height:34, borderRadius:17, justifyContent:"center", alignItems:"center" }}
                        >
                          <Text fontSize="13px" fontWeight="600" color="white">{day}</Text>
                        </LinearGradient>
                      ) : day ? (
                        <Text fontSize="13px" color={past ? "#CCC" : "#000"}>{day}</Text>
                      ) : null}
                    </Box>
                  </Pressable>
                );
              })}
            </HStack>
          ))}
        </VStack>

        <BottomRow onBack={() => onNavigate("home")} right={<SmallGradientButton label="Next" onPress={handleNext} disabled={!selectedDate} />} />
      </VStack>
    </VStack>
  );
};


const ITEM_H = 44;
const PICKER_H = 176; 
const PICKER_PAD = ITEM_H * 1.5; 

const SelectTimeScreen = ({ onNavigate, onTimeSelect }) => {
  const [hours,   setHours]   = useState(12);
  const [minutes, setMinutes] = useState(0);
  const [period,  setPeriod]  = useState("AM");

  const hourRef   = useRef(null);
  const minRef    = useRef(null);
  const periodRef = useRef(null);

  const hoursArr   = Array.from({ length: 12 }, (_, i) => i + 1); 
  const minutesArr = Array.from({ length: 60 }, (_, i) => i);      
  const periodsArr = ["AM", "PM"];

  useEffect(() => {
    const hIdx = hoursArr.indexOf(12);   
    const mIdx = 0;
    const pIdx = 0;                       
    const delay = 150;
    setTimeout(() => {
      hourRef.current?.scrollTo({ y: hIdx * ITEM_H, animated: false });
    }, delay);
    setTimeout(() => {
      minRef.current?.scrollTo({ y: mIdx * ITEM_H, animated: false });
    }, delay);
    setTimeout(() => {
      periodRef.current?.scrollTo({ y: pIdx * ITEM_H, animated: false });
    }, delay);
  }, []);

  const convertTo24 = (h, m, ap) => {
    let hr = parseInt(h);
    if (ap === "PM" && hr !== 12) hr += 12;
    if (ap === "AM" && hr === 12) hr = 0;
    return `${String(hr).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
  };

  const handleOK = () => {
    onTimeSelect(convertTo24(hours, minutes, period));
    onNavigate("selectPayment");
  };


  const DrumCol = ({ scrollRef, items, selected, onSelect, fmt, colW }) => {
    return (
      <Box width={colW} height={PICKER_H} overflow="hidden" position="relative">

        <Box
          position="absolute"
          left={0} right={0}
          style={{ top: PICKER_PAD }}
          height="1px"
          bg="#333"
          zIndex={10}
          pointerEvents="none"
        />
        <Box
          position="absolute"
          left={0} right={0}
          style={{ top: PICKER_PAD + ITEM_H }}
          height="1px"
          bg="#333"
          zIndex={10}
          pointerEvents="none"
        />

        <ScrollView
          ref={scrollRef}
          showsVerticalScrollIndicator={false}
          snapToInterval={ITEM_H}
          decelerationRate="fast"
          contentContainerStyle={{
            paddingTop: PICKER_PAD,
            paddingBottom: PICKER_PAD,
          }}
          onMomentumScrollEnd={(e) => {
            const idx = Math.round(e.nativeEvent.contentOffset.y / ITEM_H);
            const clamped = Math.max(0, Math.min(idx, items.length - 1));
            onSelect(items[clamped]);
          }}
          onScrollEndDrag={(e) => {
            const idx = Math.round(e.nativeEvent.contentOffset.y / ITEM_H);
            const clamped = Math.max(0, Math.min(idx, items.length - 1));
            onSelect(items[clamped]);
          }}
        >
          {items.map((item, i) => {
            const isSel = item === selected;
            return (
              <Pressable
                key={i}
                onPress={() => {
                  onSelect(item);
                  scrollRef.current?.scrollTo({ y: i * ITEM_H, animated: true });
                }}
              >
                <Box
                  width={colW}
                  height={ITEM_H}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Text
                    fontSize={isSel ? "22px" : "17px"}
                    fontWeight={isSel ? "600" : "300"}
                    color={isSel ? "#000000" : "#C0C0C0"}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    {fmt ? fmt(item) : String(item).padStart(2, "0")}
                  </Text>
                </Box>
              </Pressable>
            );
          })}
        </ScrollView>
      </Box>
    );
  };

  return (
    <VStack w="100%" alignItems="center" space={3}>
      <TopRow onHome={() => onNavigate("home")} />
      <VStack w="100%" maxW="360px" alignSelf="center" alignItems="center" space={4}>
        <Text fontSize="15px" fontWeight="600" color="#000" fontFamily={GlobalStyles.text.fontFamily}>
          Select Time
        </Text>

        <HStack alignItems="center" justifyContent="center">

          <DrumCol
            scrollRef={hourRef}
            items={hoursArr}
            selected={hours}
            onSelect={(h) => setHours(h)}
            colW={68}
          />

          <Box width={22} height={PICKER_H} justifyContent="center" alignItems="center">
            <Text fontSize="22px" fontWeight="bold" color="#333">:</Text>
          </Box>

          <DrumCol
            scrollRef={minRef}
            items={minutesArr}
            selected={minutes}
            onSelect={(m) => setMinutes(m)}
            colW={68}
          />

          <Box width={10} />

          <DrumCol
            scrollRef={periodRef}
            items={periodsArr}
            selected={period}
            onSelect={(p) => setPeriod(p)}
            fmt={(item) => item}   
            colW={52}
          />
        </HStack>

        <HStack space={3} justifyContent="center">
          <OutlineButton label="Cancel" onPress={() => onNavigate("selectDate")} />
          <SmallGradientButton label="OK" onPress={handleOK} />
        </HStack>
      </VStack>
    </VStack>
  );
};


const SelectPaymentScreen = ({ onNavigate, onPaymentSelect }) => {
  const [paymentOption, setPaymentOption] = useState("now");

  const handleGo = () => {
    onPaymentSelect(paymentOption);
    onNavigate("selectCategory");
  };

  return (
    <VStack w="100%" alignItems="center" space={5}>
      <TopRow onHome={() => onNavigate("home")} />
      <VStack w="100%" maxW="360px" alignSelf="center" alignItems="center" space={5}>
        <Text fontSize="13px" fontWeight="400" color="#000" textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>
          Pay now or at spot ( both options enabled )
        </Text>
        <VStack space={3} w="100%" alignItems="flex-start" px={4}>
          {[
            { val: "now",     label: "Now" },
            { val: "at_spot", label: "At spot" },
          ].map(opt => (
            <Pressable key={opt.val} onPress={() => setPaymentOption(opt.val)}>
              <HStack alignItems="center" space={3}>
                <Icon
                  as={Ionicons}
                  name={paymentOption === opt.val ? "checkbox-outline" : "square-outline"}
                  size="lg" color="#000"
                />
                <Text fontSize="14px" color="#000" fontFamily={GlobalStyles.text.fontFamily}>{opt.label}</Text>
              </HStack>
            </Pressable>
          ))}
        </VStack>
        <BottomRow
          onBack={() => onNavigate("selectTime")}
          right={<SmallGradientButton label="Go" onPress={handleGo} />}
        />
      </VStack>
    </VStack>
  );
};

const SelectCategoryScreen = ({ onNavigate, selectedDate, selectedTime, paymentOption, onCategorySelect }) => {
  const [categories,  setCategories]  = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [isLoading,   setIsLoading]   = useState(true);
  const toast = useToast();
  const user  = useSelector(s => s.user.user);

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/categories`, { headers: { Authorization: `Bearer ${user?.token}` } });
      if (res.data?.data) setCategories(res.data.data);
    } catch {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message="Failed to load categories!" />, placement: "top" });
    } finally { setIsLoading(false); }
  };

  const handleContinue = () => {
    if (!selectedCat) return;
    onCategorySelect(selectedCat);
    onNavigate("selectSeat");
  };

  return (
    <VStack w="100%" alignItems="center" space={3}>
      <TopRow onHome={() => onNavigate("home")} />
      <VStack w="100%" maxW="360px" alignSelf="center" space={3}>
        <Text fontSize="15px" fontWeight="600" color="#000" textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>Select Category</Text>
        {isLoading
          ? <Spinner color={GlobalStyles.colors.blue} size="lg" />
          : (
            <VStack w="100%" space={2}>
              {categories.map(cat => {
                const isSel = selectedCat?.id === cat.id;
                return (
                  <Pressable key={cat.id} onPress={() => setSelectedCat(cat)}>
                    <Box borderRadius={8} borderWidth={1.5} borderColor={isSel ? BLUE_DARK : "#E0E0E0"} p={3}>
                      <HStack justifyContent="space-between" alignItems="center">
                        <VStack flex={1} space={1}>
                          {cat.image && (
                            <Image source={{ uri: cat.image }} alt={cat.name} style={{ width:44,height:44,borderRadius:8 }} resizeMode="cover" mb={1} />
                          )}
                          <Text fontSize="13px" fontWeight="500" color={isSel ? BLUE_DARK : "#000"} fontFamily={GlobalStyles.text.fontFamily}>{cat.name}</Text>
                          {cat.description && <Text fontSize="11px" color="#666" fontFamily={GlobalStyles.text.fontFamily}>{cat.description}</Text>}
                          <Text fontSize="10px" color="#999" fontFamily={GlobalStyles.text.fontFamily}>{cat.objects?.length || 0} seats available</Text>
                        </VStack>
                        <Icon as={Ionicons} name={isSel ? "checkmark-circle" : "ellipse-outline"} size="md" color={isSel ? BLUE_DARK : "#CCC"} />
                      </HStack>
                    </Box>
                  </Pressable>
                );
              })}
            </VStack>
          )
        }
        {!isLoading && (
          <BottomRow
            onBack={() => onNavigate("selectPayment")}
            right={<SmallGradientButton label="Continue" onPress={handleContinue} disabled={!selectedCat} />}
          />
        )}
      </VStack>
    </VStack>
  );
};

const SelectSeatScreen = ({ onNavigate, selectedDate, selectedTime, paymentOption, selectedCategory, onSeatSelect }) => {
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [isLoading,    setIsLoading]    = useState(true);
  const [bookedSeats,  setBookedSeats]  = useState([]);
  const toast = useToast();
  const user  = useSelector(s => s.user.user);

  useEffect(() => { checkAvailability(); }, [selectedDate, selectedTime]);

  const checkAvailability = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/reservations`, { headers: { Authorization: `Bearer ${user?.token}` } });
      if (res.data && Array.isArray(res.data)) {
        setBookedSeats(
          res.data.filter(r => r.date === selectedDate && r.time === selectedTime).map(r => r.seat_no)
        );
      }
    } catch { /* silent */ }
    finally { setIsLoading(false); }
  };

  const seats          = selectedCategory?.objects || [];
  const isAvailable    = (name) => !bookedSeats.includes(name);
  const allUnavailable = seats.length > 0 && seats.every(s => !isAvailable(s.name));

  const handleSeatPress = (seat) => {
    if (!isAvailable(seat.name)) {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message="Seat not available for selected date/time!" />, placement: "top" });
      return;
    }
    setSelectedSeat(seat);
  };

  const handleNext = () => {
    if (!selectedSeat) return;
    onSeatSelect(selectedSeat);
    onNavigate(paymentOption === "now" ? "paymentForm" : "confirmReservation");
  };

  const rows = [];
  for (let i = 0; i < seats.length; i += 2) rows.push([seats[i], seats[i+1] || null]);

  return (
    <VStack w="100%" alignItems="center" space={3}>
      <TopRow onHome={() => onNavigate("home")} />
      <VStack w="100%" maxW="300px" alignSelf="center" space={3}>
        <Text fontSize="15px" fontWeight="600" color="#000" textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>
          Select from following
        </Text>

        {isLoading
          ? <Spinner color={GlobalStyles.colors.blue} size="lg" />
          : (
            <VStack w="100%">
              {rows.map((row, ri) => (
                <HStack key={ri} w="100%">
                  {row.map((seat, si) => {
                    if (!seat) return <Box key={si} flex={1} h="44px" />;
                    const avail = isAvailable(seat.name);
                    const sel   = selectedSeat?.id === seat.id;
                    return (
                      <Pressable key={seat.id} flex={1} onPress={() => handleSeatPress(seat)} disabled={!avail}>
                        <Box
                          flex={1}
                          borderWidth={1}
                          borderColor={sel ? BLUE_DARK : "#000"}
                          m="1px"
                          h="44px"
                          justifyContent="center"
                          alignItems="center"
                          bg={sel ? BLUE_DARK : "transparent"}
                          opacity={avail ? 1 : 0.3}
                        >
                          <Text
                            fontSize="13px"
                            fontWeight={sel ? "600" : "400"}
                            color={sel ? MUSTARD : "#000"}
                            fontFamily={GlobalStyles.text.fontFamily}
                          >
                            {seat.name}
                          </Text>
                        </Box>
                      </Pressable>
                    );
                  })}
                </HStack>
              ))}
              {allUnavailable && (
                <Text fontSize="12px" color="#666" mt={3} textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>
                  No seats available for the selected date and time.
                </Text>
              )}
            </VStack>
          )
        }

        {!isLoading && (
          <BottomRow
            onBack={() => onNavigate("selectCategory")}
            right={<SmallGradientButton label="Next" onPress={handleNext} disabled={!selectedSeat} />}
          />
        )}
      </VStack>
    </VStack>
  );
};

const PaymentFormScreen = ({ onNavigate, selectedDate, selectedTime, selectedSeat, onPaymentComplete }) => {
  const [cardType,       setCardType]       = useState("visa");
  const [cardHolderName, setCardHolderName] = useState("");
  const [cardNumber,     setCardNumber]     = useState("");
  const [expiryDate,     setExpiryDate]     = useState("");
  const [cvv,            setCvv]            = useState("");
  const [isProcessing,   setIsProcessing]   = useState(false);
  const toast = useToast();
  const user  = useSelector(s => s.user.user);

  const formatCard   = t => t.replace(/\D/g,"").substring(0,16).replace(/.{1,4}/g,g=>g+" ").trim();
  const formatExpiry = t => { const c=t.replace(/\D/g,"").substring(0,4); return c.length>=2?c.slice(0,2)+"/"+c.slice(2):c; };

  const handleConfirm = async () => {
    if (!cardHolderName || !cardNumber || !expiryDate || !cvv) {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message="Please fill all payment details!" />, placement: "top" });
      return;
    }
    setIsProcessing(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/reservations`,
        { customer_name: cardHolderName, date: selectedDate, time: selectedTime, seat_no: selectedSeat.name, payment_type: "NOW" },
        { headers: { Authorization: `Bearer ${user?.token}`, "Content-Type": "application/json" } },
      );
      if (res.data?.reservation) {
        toast.show({ render: () => <Toast color={GlobalStyles.colors.green} message="Payment successful!" />, placement: "top" });
        onPaymentComplete(res.data.reservation);
        onNavigate("paymentSuccess");
      }
    } catch (err) {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message={err.response?.data?.message || "Payment failed."} />, placement: "top" });
      onNavigate("paymentFailed");
    } finally { setIsProcessing(false); }
  };

  const FieldInput = ({ placeholder, value, onChangeText, keyboardType, maxLength, secureTextEntry }) => (
    <Input
      placeholder={placeholder} placeholderTextColor="#999"
      fontSize="13px" color="#000"
      borderColor="#CCC" borderWidth={1} borderRadius={6}
      px={3} py={2} bg="white"
      value={value} onChangeText={onChangeText}
      keyboardType={keyboardType} maxLength={maxLength} secureTextEntry={secureTextEntry}
    />
  );

  return (
    <ScrollView w="100%">
      <VStack w="100%" space={4}>
        <TopRow onHome={() => onNavigate("home")} />
        <HStack w="100%" space={4} alignItems="flex-start" flexWrap="wrap">
          <VStack flex={1} space={3} minW="200px">
            <HStack alignItems="center" space={2}>
              <Icon as={Ionicons} name="card-outline" size="sm" color="#000" />
              <Text fontSize="14px" fontWeight="600" color="#000" fontFamily={GlobalStyles.text.fontFamily}>Payment</Text>
            </HStack>
            <VStack space={1}>
              <Text fontSize="12px" color="#666" fontFamily={GlobalStyles.text.fontFamily}>Card type</Text>
              <HStack space={4}>
                {["visa","mastercard"].map(ct => (
                  <Pressable key={ct} onPress={() => setCardType(ct)}>
                    <HStack alignItems="center" space={2}>
                      <Icon as={Ionicons} name={cardType===ct?"radio-button-on":"radio-button-off"} size="sm" color="#000"/>
                      {ct==="visa"
                        ? <Box borderRadius={4} bg="#1A1F71" px={2} py={0.5}><Text fontSize="11px" fontWeight="bold" color="white">VISA</Text></Box>
                        : <HStack space={-2}><Box w="22px" h="22px" borderRadius="full" bg="#EB001B"/><Box w="22px" h="22px" borderRadius="full" bg="#FF5F00"/></HStack>
                      }
                    </HStack>
                  </Pressable>
                ))}
              </HStack>
            </VStack>
            <VStack space={1}>
              <Text fontSize="12px" color="#666" fontFamily={GlobalStyles.text.fontFamily}>Card holder's name</Text>
              <FieldInput placeholder="Name on card" value={cardHolderName} onChangeText={setCardHolderName} />
            </VStack>
            <VStack space={1}>
              <Text fontSize="12px" color="#666" fontFamily={GlobalStyles.text.fontFamily}>Card number</Text>
              <FieldInput placeholder="---- ---- ---- ----" value={cardNumber} onChangeText={t=>setCardNumber(formatCard(t))} keyboardType="numeric" maxLength={19} />
            </VStack>
            <HStack space={3}>
              <VStack flex={1} space={1}>
                <Text fontSize="12px" color="#666" fontFamily={GlobalStyles.text.fontFamily}>Expiry</Text>
                <FieldInput placeholder="MM/YY" value={expiryDate} onChangeText={t=>setExpiryDate(formatExpiry(t))} maxLength={5} />
              </VStack>
              <VStack flex={1} space={1}>
                <Text fontSize="12px" color="#666" fontFamily={GlobalStyles.text.fontFamily}>CVV</Text>
                <FieldInput placeholder="Code" value={cvv} onChangeText={setCvv} keyboardType="numeric" maxLength={3} secureTextEntry />
              </VStack>
            </HStack>
          </VStack>

          <VStack flex={1} space={2} minW="160px" alignItems="center">
            <Text fontSize="13px" fontWeight="600" color="#000" fontFamily={GlobalStyles.text.fontFamily}>Summary</Text>
            <Icon as={Ionicons} name="document-text-outline" size="3xl" color={BLUE_DARK} />
            <Text fontSize="13px" fontWeight="600" color={BLUE_DARK} fontFamily={GlobalStyles.text.fontFamily}>Reservation</Text>
            <VStack w="100%" space={1.5} mt={1}>
              <HStack justifyContent="space-between">
                <Text fontSize="12px" color="#666" fontFamily={GlobalStyles.text.fontFamily}>Base:</Text>
                <Text fontSize="12px" fontWeight="500" color="#000">15 USD</Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontSize="12px" color="#666" fontFamily={GlobalStyles.text.fontFamily}>Tax:</Text>
                <Text fontSize="12px" fontWeight="500" color="#000">4 USD</Text>
              </HStack>
              <Box h="1px" bg="#CCC" my={1} />
              <HStack justifyContent="space-between">
                <Text fontSize="13px" fontWeight="600" color="#000" fontFamily={GlobalStyles.text.fontFamily}>Total:</Text>
                <Text fontSize="13px" fontWeight="600" color="#000">19 USD</Text>
              </HStack>
            </VStack>
            <SmallGradientButton label="Confirm" onPress={handleConfirm} isLoading={isProcessing} />
          </VStack>
        </HStack>

        <HStack justifyContent="flex-start" mt={2}>
          <OutlineButton label="Back" onPress={() => onNavigate("selectSeat")} />
        </HStack>
      </VStack>
    </ScrollView>
  );
};


const ConfirmReservationScreen = ({ onNavigate, selectedDate, selectedTime, selectedSeat, onReservationComplete }) => {
  const [customerName, setCustomerName] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const toast = useToast();
  const user  = useSelector(s => s.user.user);

  const handleConfirm = async () => {
    if (!customerName.trim()) {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message="Please enter your name!" />, placement: "top" });
      return;
    }
    setIsProcessing(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/reservations`,
        { customer_name: customerName.trim(), date: selectedDate, time: selectedTime, seat_no: selectedSeat.name, payment_type: "LATER" },
        { headers: { Authorization: `Bearer ${user?.token}`, "Content-Type": "application/json" } },
      );
      if (res.data?.reservation) {
        toast.show({ render: () => <Toast color={GlobalStyles.colors.green} message="Reservation confirmed!" />, placement: "top" });
        onReservationComplete(res.data.reservation);
        onNavigate("paymentSuccess");
      }
    } catch (err) {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message={err.response?.data?.message || "Failed to create reservation."} />, placement: "top" });
    } finally { setIsProcessing(false); }
  };

  return (
    <VStack w="100%" alignItems="center" space={4}>
      <TopRow onHome={() => onNavigate("home")} />
      <VStack w="100%" maxW="360px" alignSelf="center" space={4}>
        <Text fontSize="15px" fontWeight="600" color="#000" textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>Confirm Reservation</Text>
        <VStack space={1.5} w="100%">
          <DetailRow label="Date:"    value={selectedDate} />
          <DetailRow label="Time:"    value={selectedTime} />
          <DetailRow label="Seat:"    value={selectedSeat?.name} />
          <DetailRow label="Payment:" value="At Spot" />
        </VStack>
        <Input
          placeholder="Enter Your Name"
          fontSize="13px" color="#000"
          borderColor="#CCC" borderWidth={1} borderRadius={8}
          px={3} py={2} w="100%"
          value={customerName}
          onChangeText={setCustomerName}
          bg="white"
          fontFamily={GlobalStyles.text.fontFamily}
        />
        <BottomRow
          onBack={() => onNavigate("selectSeat")}
          right={<SmallGradientButton label="Confirm" onPress={handleConfirm} isLoading={isProcessing} />}
        />
      </VStack>
    </VStack>
  );
};

const PaymentSuccessScreen = ({ onNavigate, reservationDetails }) => (
  <VStack w="100%" alignItems="center" space={4} mt={2}>
    <HStack w="100%" justifyContent="flex-end"><HomeButton onPress={() => onNavigate("home")} /></HStack>
    <VStack w="100%" maxW="360px" alignSelf="center" alignItems="center" space={4}>
      <Text fontSize="15px" fontWeight="600" color="#000" fontFamily={GlobalStyles.text.fontFamily}>Payment Successful</Text>
      {reservationDetails?.qr_code && (
        <VStack space={1.5} alignItems="center">
          <Image source={{ uri: reservationDetails.qr_code }} alt="QR" style={{ width:140,height:140 }} resizeMode="contain" />
          <Text fontSize="11px" color="#666" textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>
            Take a picture of your QR code
          </Text>
        </VStack>
      )}
      <Text fontSize="13px" color="#000" fontFamily={GlobalStyles.text.fontFamily}>
        Reservation ID: {reservationDetails?.id || "N/A"}
      </Text>
      <Text fontSize="12px" color="#666" textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>
        Details sent to your email.
      </Text>
      <SmallGradientButton label="Done" onPress={() => onNavigate("home")} />
    </VStack>
  </VStack>
);

const PaymentFailedScreen = ({ onNavigate }) => (
  <VStack w="100%" alignItems="center" space={5} mt={2}>
    <HStack w="100%" justifyContent="flex-end"><HomeButton onPress={() => onNavigate("home")} /></HStack>
    <VStack w="100%" maxW="360px" alignSelf="center" alignItems="center" space={5}>
      <Text fontSize="18px" fontWeight="600" color="#000" fontFamily={GlobalStyles.text.fontFamily}>Payment Not Successful</Text>
      <SmallGradientButton label="Try Again" onPress={() => onNavigate("paymentForm")} />
    </VStack>
  </VStack>
);

const ViewReservationScreen = ({ onNavigate }) => {
  const [query,      setQuery]      = useState("");
  const [details,    setDetails]    = useState(null);
  const [isLoading,  setIsLoading]  = useState(false);
  const [showEdit,   setShowEdit]   = useState(false);
  const [editData,   setEditData]   = useState({ customer_name:"", date:"", time:"", seat_no:"", payment_type:"LATER" });
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();
  const user  = useSelector(s => s.user.user);

  const searchReservation = async () => {
    const q = query.trim();
    if (!q) {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message="Enter reservation ID or name!" />, placement: "top" });
      return;
    }
    setIsLoading(true);
    setDetails(null);
    try {
      try {
        const byId = await axios.get(`${API_BASE_URL}/api/reservations/${q}`, { headers: { Authorization: `Bearer ${user?.token}` } });
        if (byId.data?.id) {
          setDetails(byId.data);
          setEditData({ customer_name:byId.data.customer_name, date:byId.data.date, time:byId.data.time, seat_no:byId.data.seat_no, payment_type:(byId.data.payment_type||"LATER").toUpperCase() });
          setIsLoading(false); return;
        }
      } catch { 
        
       }

      const all = await axios.get(`${API_BASE_URL}/api/reservations`, { headers: { Authorization: `Bearer ${user?.token}` } });
      if (all.data && Array.isArray(all.data)) {
        const qLow  = q.toLowerCase();
        const found = all.data.find(r => r.id.toLowerCase()===qLow || r.customer_name.toLowerCase().includes(qLow));
        if (found) {
          setDetails(found);
          setEditData({ customer_name:found.customer_name, date:found.date, time:found.time, seat_no:found.seat_no, payment_type:(found.payment_type||"LATER").toUpperCase() });
        } else {
          toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message="Reservation not found!" />, placement: "top" });
        }
      }
    } catch {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message="Reservation not found!" />, placement: "top" });
    } finally { setIsLoading(false); }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      const payload = { ...editData, payment_type: (editData.payment_type||"LATER").toUpperCase() };
      const res = await axios.put(
        `${API_BASE_URL}/api/reservations/${details.id}`,
        payload,
        { headers: { Authorization: `Bearer ${user?.token}`, "Content-Type": "application/json" } },
      );
      if (res.data?.reservation) {
        setDetails(res.data.reservation);
        setShowEdit(false);
        toast.show({ render: () => <Toast color={GlobalStyles.colors.green} message="Updated successfully!" />, placement: "top" });
      }
    } catch (err) {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message={err.response?.data?.message||"Update failed."} />, placement: "top" });
    } finally { setIsUpdating(false); }
  };

  return (
    <VStack w="100%" alignItems="center" space={3}>
      <TopRow onHome={() => onNavigate("home")} />
      <VStack w="100%" maxW="360px" alignSelf="center" space={3}>

        <HStack w="100%" space={2} alignItems="center">
          <SearchBar value={query} onChange={setQuery} onClear={() => { setQuery(""); setDetails(null); }} onSubmit={searchReservation} />
          <SmallGradientButton label="Search" onPress={searchReservation} />
        </HStack>

        {isLoading && <Center py={4}><Spinner color={GlobalStyles.colors.blue} size="lg" /></Center>}

        {details && !isLoading && (
          <VStack space={3} w="100%">
            <VStack space={1.5} w="100%">
              <DetailRow label="Reservation ID:" value={details.id} />
              <DetailRow label="Customer:"       value={details.customer_name} />
              <DetailRow label="Date:"           value={details.date} />
              <DetailRow label="Time:"           value={details.time} />
              <DetailRow label="Seat:"           value={details.seat_no} />
              <DetailRow label="Payment:"        value={details.payment_type} />
              <DetailRow label="Status:"         value="Confirmed" valueColor="#4CAF50" />
            </VStack>
            <HStack space={3} justifyContent="flex-end" w="100%">
              <OutlineButton label="Edit" onPress={() => setShowEdit(true)} />
              <SmallGradientButton label="Done" onPress={() => onNavigate("home")} />
            </HStack>
          </VStack>
        )}

        <HStack justifyContent="flex-start" mt={2}>
          <OutlineButton label="Back" onPress={() => onNavigate("home")} />
        </HStack>
      </VStack>

      <Modal isOpen={showEdit} onClose={() => setShowEdit(false)} size="lg">
        <Modal.Content maxWidth="340px">
          <Modal.CloseButton />
          <Modal.Header>
            <Text fontSize="13px" fontWeight="600" color="#000" fontFamily={GlobalStyles.text.fontFamily}>Edit Reservation</Text>
          </Modal.Header>
          <Modal.Body>
            <VStack space={3}>
              {[
                { label:"Customer Name",     field:"customer_name", ph:"Full name" },
                { label:"Date (YYYY-MM-DD)", field:"date",          ph:"2025-01-15" },
                { label:"Time (HH:MM:SS)",   field:"time",          ph:"14:30:00" },
                { label:"Seat",              field:"seat_no",       ph:"Seat name" },
              ].map(f => (
                <VStack key={f.field} space={1}>
                  <Text fontSize="11px" color="#666" fontFamily={GlobalStyles.text.fontFamily}>{f.label}</Text>
                  <Input
                    value={editData[f.field]}
                    onChangeText={t => setEditData({ ...editData, [f.field]: t })}
                    placeholder={f.ph} fontSize="13px" borderColor="#CCC" borderRadius={4}
                  />
                </VStack>
              ))}
              <VStack space={1}>
                <Text fontSize="11px" color="#666" fontFamily={GlobalStyles.text.fontFamily}>Payment Type</Text>
                <HStack space={4}>
                  {["NOW","LATER"].map(pt => (
                    <Pressable key={pt} onPress={() => setEditData({ ...editData, payment_type: pt })}>
                      <HStack alignItems="center" space={2}>
                        <Icon as={Ionicons} name={editData.payment_type===pt?"radio-button-on":"radio-button-off"} size="sm" color={BLUE_DARK} />
                        <Text fontSize="13px" color={BLUE_DARK} fontFamily={GlobalStyles.text.fontFamily}>{pt}</Text>
                      </HStack>
                    </Pressable>
                  ))}
                </HStack>
              </VStack>
              <HStack space={3} justifyContent="flex-end" mt={1}>
                <OutlineButton label="Cancel" onPress={() => setShowEdit(false)} disabled={isUpdating} />
                <SmallGradientButton label="Update" onPress={handleUpdate} isLoading={isUpdating} />
              </HStack>
            </VStack>
          </Modal.Body>
        </Modal.Content>
      </Modal>
    </VStack>
  );
};


const CancelReservationScreen = ({ onNavigate }) => {
  const [query,       setQuery]       = useState("");
  const [details,     setDetails]     = useState(null);
  const [isLoading,   setIsLoading]   = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDeleting,  setIsDeleting]  = useState(false);
  const toast = useToast();
  const user  = useSelector(s => s.user.user);

  const searchReservation = async () => {
    const q = query.trim();
    if (!q) {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message="Enter reservation ID or name!" />, placement: "top" });
      return;
    }
    setIsLoading(true);
    setDetails(null);
    try {
      try {
        const byId = await axios.get(`${API_BASE_URL}/api/reservations/${q}`, { headers: { Authorization: `Bearer ${user?.token}` } });
        if (byId.data?.id) { setDetails(byId.data); setIsLoading(false); return; }
      } catch { /* fall through */ }
      const all = await axios.get(`${API_BASE_URL}/api/reservations`, { headers: { Authorization: `Bearer ${user?.token}` } });
      if (all.data && Array.isArray(all.data)) {
        const qLow  = q.toLowerCase();
        const found = all.data.find(r => r.id.toLowerCase()===qLow || r.customer_name.toLowerCase().includes(qLow));
        if (found) setDetails(found);
        else toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message="Reservation not found!" />, placement: "top" });
      }
    } catch {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message="Reservation not found!" />, placement: "top" });
    } finally { setIsLoading(false); }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/reservations/${details.id}`, { headers: { Authorization: `Bearer ${user?.token}` } });
      toast.show({ render: () => <Toast color={GlobalStyles.colors.green} message="Reservation cancelled!" />, placement: "top" });
      setShowConfirm(false); setDetails(null); setQuery("");
    } catch (err) {
      toast.show({ render: () => <Toast color={GlobalStyles.colors.red} message={err.response?.data?.message||"Failed to cancel."} />, placement: "top" });
    } finally { setIsDeleting(false); }
  };

  return (
    <VStack w="100%" alignItems="center" space={3}>
      <TopRow onHome={() => onNavigate("home")} />
      <VStack w="100%" maxW="360px" alignSelf="center" space={3}>

        <HStack w="100%" space={2} alignItems="center">
          <SearchBar value={query} onChange={setQuery} onClear={() => { setQuery(""); setDetails(null); }} onSubmit={searchReservation} />
          <SmallGradientButton label="Search" onPress={searchReservation} />
        </HStack>

        {isLoading && <Center py={4}><Spinner color={GlobalStyles.colors.blue} size="lg" /></Center>}

        {details && !isLoading && (
          <VStack space={3} w="100%">
            <VStack space={1.5} w="100%">
              <DetailRow label="Reservation ID:" value={details.id} />
              <DetailRow label="Customer:"       value={details.customer_name} />
              <DetailRow label="Date:"           value={details.date} />
              <DetailRow label="Time:"           value={details.time} />
              <DetailRow label="Seat:"           value={details.seat_no} />
              <DetailRow label="Payment:"        value={details.payment_type} />
            </VStack>
            <HStack space={3} alignItems="center" justifyContent="center">
              <Text fontSize="12px" color="#555" fontFamily={GlobalStyles.text.fontFamily}>Are you sure to cancel?</Text>
              <SmallGradientButton label="Yes" onPress={() => setShowConfirm(true)} />
            </HStack>
          </VStack>
        )}

        <HStack justifyContent="flex-start" mt={2}>
          <OutlineButton label="Back" onPress={() => onNavigate("home")} />
        </HStack>
      </VStack>

      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} size="sm">
        <Modal.Content maxWidth="280px">
          <Modal.CloseButton />
          <Modal.Header>
            <Text fontSize="13px" fontWeight="600" color="#000" fontFamily={GlobalStyles.text.fontFamily}>Confirm Cancellation</Text>
          </Modal.Header>
          <Modal.Body>
            <VStack space={4} pb={1} alignItems="center">
              <Text fontSize="12px" color="#666" textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>
                This will permanently cancel your reservation. Continue?
              </Text>
              <HStack space={3}>
                <OutlineButton label="No"          onPress={() => setShowConfirm(false)} disabled={isDeleting} />
                <SmallGradientButton label="Yes, Cancel" onPress={handleDelete} isLoading={isDeleting} />
              </HStack>
            </VStack>
          </Modal.Body>
        </Modal.Content>
      </Modal>
    </VStack>
  );
};


const CitizenReservation = ({ onBack }) => {
  const [screen,             setScreen]             = useState("home");
  const [selectedDate,       setSelectedDate]       = useState(null);
  const [selectedTime,       setSelectedTime]       = useState(null);
  const [paymentOption,      setPaymentOption]      = useState(null);
  const [selectedCategory,   setSelectedCategory]   = useState(null);
  const [selectedSeat,       setSelectedSeat]       = useState(null);
  const [reservationDetails, setReservationDetails] = useState(null);

  const nav = (s) => setScreen(s);

  const renderScreen = () => {
    switch (screen) {
      case "home":               return <HomeScreen onNavigate={nav} />;
      case "makeReservation":
      case "selectDate":         return <SelectDateScreen onNavigate={nav} onDateSelect={setSelectedDate} />;
      case "selectTime":         return <SelectTimeScreen onNavigate={nav} selectedDate={selectedDate} onTimeSelect={setSelectedTime} />;
      case "selectPayment":      return <SelectPaymentScreen onNavigate={nav} onPaymentSelect={setPaymentOption} />;
      case "selectCategory":     return <SelectCategoryScreen onNavigate={nav} selectedDate={selectedDate} selectedTime={selectedTime} paymentOption={paymentOption} onCategorySelect={setSelectedCategory} />;
      case "selectSeat":         return <SelectSeatScreen onNavigate={nav} selectedDate={selectedDate} selectedTime={selectedTime} paymentOption={paymentOption} selectedCategory={selectedCategory} onSeatSelect={setSelectedSeat} />;
      case "paymentForm":        return <PaymentFormScreen onNavigate={nav} selectedDate={selectedDate} selectedTime={selectedTime} selectedSeat={selectedSeat} onPaymentComplete={setReservationDetails} />;
      case "confirmReservation": return <ConfirmReservationScreen onNavigate={nav} selectedDate={selectedDate} selectedTime={selectedTime} selectedSeat={selectedSeat} onReservationComplete={setReservationDetails} />;
      case "paymentSuccess":     return <PaymentSuccessScreen onNavigate={nav} reservationDetails={reservationDetails} />;
      case "paymentFailed":      return <PaymentFailedScreen onNavigate={nav} />;
      case "viewReservation":    return <ViewReservationScreen onNavigate={nav} />;
      case "cancelReservation":  return <CancelReservationScreen onNavigate={nav} />;
      default:                   return <HomeScreen onNavigate={nav} />;
    }
  };

  return (
    <ScrollView w="100%" flex={1}>
      <Center w="100%" py={3}>
        <Box w="100%" maxW="500px" px={4}>
          {onBack && screen === "home" && (
            <Pressable onPress={onBack} mb={2}>
              <HStack alignItems="center" space={1}>
                <Icon as={Ionicons} name="chevron-back" size="xs" color={GlobalStyles.colors.blue} />
                <Text color={GlobalStyles.colors.blue} fontWeight="500" fontSize="12px" fontFamily={GlobalStyles.text.fontFamily}>
                  Back to Services
                </Text>
              </HStack>
            </Pressable>
          )}
          {renderScreen()}
        </Box>
      </Center>
    </ScrollView>
  );
};

export default CitizenReservation;